/**
 * Decides whether text typed in the toolbar bar is a search or a message for
 * Partnr. Pure and synchronous: no network call, runs on every keystroke.
 */

export type BarIntent = 'search' | 'partnr';

export interface IntentResult {
  intent: BarIntent;
  /** The text to search for or send, with any forcing prefix removed. */
  text: string;
  /** True when a prefix ("?", "/ask", "/", "#") decided the intent. */
  forced: boolean;
}

export interface IntentSignals {
  /** Local search hits for this exact text; undefined while unknown. */
  resultCount?: number;
}

const QUESTION_WORDS = [
  'what', 'how', 'why', 'when', 'where', 'who', 'whom', 'whose', 'which',
  'can', 'could', 'should', 'would', 'will', 'is', 'are', 'was', 'were',
  'do', 'does', 'did', 'have', 'has', 'am',
];

// Verbs that start a request. "find" and "search" are left out: they read as
// a search more often than a message.
const IMPERATIVE_VERBS = [
  'help', 'explain', 'summarise', 'summarize', 'compare', 'analyse', 'analyze',
  'create', 'write', 'generate', 'make', 'brainstorm', 'suggest',
  'tell', 'give', 'rewrite', 'improve', 'organise', 'organize', 'convert',
  'describe', 'recommend', 'critique', 'translate', 'remind', 'please',
];

// Verbs that are also common nouns ("design system", "plan q3"): they only
// count as a request when the rest reads like a sentence.
const AMBIGUOUS_VERBS = [
  'plan', 'design', 'list', 'review', 'build', 'draft', 'outline', 'turn',
  'think', 'come', 'map', 'research',
];

// Words that only show up in sentences, not keyword queries
const FUNCTION_WORDS = new Set([
  'i', 'me', 'my', 'mine', 'we', 'our', 'us', 'you', 'your', 'it', 'its',
  'the', 'a', 'an', 'this', 'that', 'these', 'those',
  'about', 'for', 'to', 'of', 'in', 'on', 'with', 'from', 'into', 'between',
  'and', 'or', 'but', 'is', 'are', 'was', 'were', 'be', 'did', 'do', 'does',
  'should', 'could', 'would', 'can', 'will', 'have', 'has', 'any', 'some',
]);

// Someone talking to someone, not describing a thing
const PERSONAL_WORDS = new Set(['i', 'me', 'my', 'we', 'our', 'us', 'you', 'your', 'please']);

const AI_PHRASES = [
  'ideas for', 'suggestions for', 'between these', 'find connections',
  'tell me', 'show me', 'give me', 'help me', 'can you', 'could you',
  'i want', 'i need', "what's", "how's",
];

function words(text: string): string[] {
  return text.toLowerCase().split(/\s+/).filter(Boolean);
}

/** Looks like a sentence rather than a keyword query. */
function isNaturalLanguage(ws: string[]): boolean {
  return ws.length >= 3 && ws.some((w) => FUNCTION_WORDS.has(w.replace(/[^a-z']/g, '')));
}

/** Written as a question or a request (imperative), whatever its length. */
export function isQuestionLikeQuery(query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return false;
  if (trimmed.endsWith('?')) return true;

  const ws = words(trimmed);
  // One bare word ("summarise", "plan") is a keyword, not a request
  if (ws.length < 2) return false;

  const first = ws[0].replace(/[^a-z']/g, '');
  if (QUESTION_WORDS.includes(first) || IMPERATIVE_VERBS.includes(first)) return true;
  if (AMBIGUOUS_VERBS.includes(first) && isNaturalLanguage(ws)) return true;
  return AI_PHRASES.some((p) => trimmed.startsWith(p) || trimmed.includes(` ${p} `));
}

/** Strips a forcing prefix and returns the intent it forces, if any. */
function parsePrefix(raw: string): { intent: BarIntent; text: string } | null {
  const lower = raw.toLowerCase();
  if (lower === '/ask' || lower.startsWith('/ask ')) {
    return { intent: 'partnr', text: raw.slice(4).trim() };
  }
  if (raw.startsWith('?')) return { intent: 'partnr', text: raw.slice(1).trim() };
  if (raw.startsWith('/') || raw.startsWith('#')) {
    return { intent: 'search', text: raw.slice(1).trim() };
  }
  return null;
}

export function detectBarIntent(query: string, signals: IntentSignals = {}): IntentResult {
  const raw = query.trim();
  if (!raw) return { intent: 'search', text: '', forced: false };

  const prefixed = parsePrefix(raw);
  if (prefixed) return { ...prefixed, forced: true };

  const ws = words(raw);
  const questionLike = isQuestionLikeQuery(raw);

  // Short questions and requests ("summarise my Research folder")
  if (questionLike && ws.length >= 3) return { intent: 'partnr', text: raw, forced: false };
  if (raw.endsWith('?')) return { intent: 'partnr', text: raw, forced: false };

  // A 5+ word request addressed to someone ("I need…", "…for my team") is a message
  if (ws.length >= 5 && (questionLike || ws.some((w) => PERSONAL_WORDS.has(w.replace(/[^a-z']/g, ''))))) {
    return { intent: 'partnr', text: raw, forced: false };
  }

  // Nothing saved matches and it reads like a sentence: Partnr can still help
  if (signals.resultCount === 0 && isNaturalLanguage(ws)) {
    return { intent: 'partnr', text: raw, forced: false };
  }

  if (questionLike && signals.resultCount === 0) {
    return { intent: 'partnr', text: raw, forced: false };
  }

  return { intent: 'search', text: raw, forced: false };
}
